import React, { useMemo, useState } from 'react';
import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { ComponentSize, FdsIcon, Icon, IconVariation } from '@tcc/fds';
import { currencyFormatter, getMessage } from 'util/locale';
import { convertForLocale, inferUnit } from 'util/unitConversion';
import {
  calculateEstimatedYield,
  calculateProjectedRevenuePerArea,
  calculateSeedCostAndBagsNeeded
} from '../../common/ScripGeneration/ReviewPlan/utils';
import { formatAvgYield, formatNumberK, formatNumSeedBags } from 'util/formatters/numberFormatter';
import { FieldData, FormData, RecommendationOptions } from '../../common/types';
import { useAppSelector } from 'app/hooks';
import { getActiveCropNominalWeight } from 'current/currentSelectors';
import { getCurrency } from 'user/userSelectors';
import { allowDecimalByCountry, getBagSizeForCrop } from 'user/selectors';
import { Control, useFieldArray } from 'react-hook-form';
import { queryClient } from 'common/providers/QueryProvider';
import {
  PrescriptionGenerationJobQueryResponse
} from 'create/bulk/common/hooks/useBulkGeneratePlantingPrescription/types';
import { JOB_STATUS_KEY } from 'create/bulk/common/hooks/useBulkGeneratePlantingPrescription/const';

interface UseReviewScriptsTableParams {
  fields: FieldData[];
  grainPrice: FormData['grainPrice'];
  control: Control<FormData>;
}

export const useReviewScriptsTable = ({ fields, grainPrice, control }: UseReviewScriptsTableParams) => {
  const cropNominalWeight = useAppSelector(getActiveCropNominalWeight);
  const bagSize = useAppSelector((state) => getBagSizeForCrop(state, 'corn'));
  const isDecimalCountry = useAppSelector(allowDecimalByCountry);
  const currency = useAppSelector(getCurrency);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const { remove } = useFieldArray<FormData, 'fields', '_id'>({
    control,
    name: 'fields',
    // useFieldArray overwrites the id (field id) from the data. To avoid that, we set a custom keyName
    keyName: '_id',
  });

  const data = useMemo(() => fields.map(({
    recommendation,
    area,
    avgFixedPopulation,
    seedCostPerBag,
    name,
    hybrid,
    selectedRecommendation,
    id,
  }) => {
    const seedingRate = selectedRecommendation === RecommendationOptions.FVSS_RECOMMENDATION ? convertForLocale({
      quantity: recommendation.recommendation.v,
      unit: recommendation.recommendation.u,
      unitType: 'seed-density',
      cropNominalWeight
    }).scalar : Number(avgFixedPopulation);
    const estimatedYield = calculateEstimatedYield({
      seedingRate,
      curve: recommendation.curve,
      cropNominalWeight
    });
    const convertedFieldTotalArea = convertForLocale({
      quantity: area.q,
      unit: area.u || inferUnit('area').unitKey,
      unitType: 'area'
    }).scalar;
    const { bagsNeeded, seedCostPerArea } = calculateSeedCostAndBagsNeeded({
      totalArea: convertedFieldTotalArea,
      avgSeedingRate: seedingRate,
      bagSize,
      seedCostPerBag: Number(seedCostPerBag)
    });

    const projectedRevenuePerArea = calculateProjectedRevenuePerArea({
      estimatedYield,
      grainPrice: Number(grainPrice),
      seedCostPerArea
    });

    const { prescriptionGenerationJob } = queryClient.getQueryData<PrescriptionGenerationJobQueryResponse>([
      JOB_STATUS_KEY,
      id
    ]);
    const { zones } = prescriptionGenerationJob.result;

    return {
      id,
      name,
      zones: zones?.length,
      hybrid: hybrid.name,
      formattedAvgFixedPopulation: formatNumberK(seedingRate),
      formattedEstimatedYield: formatAvgYield(estimatedYield, isDecimalCountry),
      formattedBagsNeeded: formatNumSeedBags(bagsNeeded),
      formattedSeedCostPerBag: currencyFormatter(seedCostPerBag, currency, { round: true }),
      formattedProjectedRevenuePerArea: currencyFormatter(projectedRevenuePerArea, currency, { round: true }),
      isRecommended: selectedRecommendation === RecommendationOptions.FVSS_RECOMMENDATION
    };
  }), [fields, grainPrice, cropNominalWeight, bagSize, isDecimalCountry, currency]);
  const columns: ColumnDef<typeof data[number]>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: getMessage('bulk-fvss-table-column_field'),
      size: 200,
      cell: ({ row }) => (
        <button className="field-cell" onClick={() => setExpanded({ [row.id]: !row.getIsExpanded() })} type='button'>
          <span data-is-open={row.getIsExpanded()}><Icon icon='arrow_drop_down' /></span>
          <span>{row.getValue('name')}</span>
        </button>
      )
    },
    {
      // TODO: implement once we get a precription with zones from the backend
      accessorKey: 'zones',
      header: getMessage('label-text__num-zones--short'),
      size: 70
    },
    {
      accessorKey: 'hybrid',
      header: getMessage('bulk-fvss-review-scripts-hybrid-table-header')
    },
    {
      accessorKey: 'formattedAvgFixedPopulation',
      header: getMessage(
        'bulk-fvss-review-scripts-avg-fixed-population-table-header',
        { unit: inferUnit('seed-density').symbol.quantitySpecific }
      ),
      size: 200,
      cell: ({ row }) => (
        <div className="avg-fixed-population-cell">
          <span>{row.original.formattedAvgFixedPopulation}</span>
          {row.original.isRecommended && (
            <span className="recommendation-label" data-test-id="recommendation-label">
              {getMessage('text__recommended')}
            </span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'formattedEstimatedYield',
      header: `${getMessage('estimated-yield-short')} (${inferUnit('area-density.yield').symbol.quantitySpecific})`,
      size: 110
    },
    {
      accessorKey: 'formattedBagsNeeded',
      header: getMessage('bags-needed'),
      size: 110,
    },
    {
      accessorKey: 'formattedSeedCostPerBag',
      header: getMessage('bulk-fvss-review-scripts-seed-cost-per-bag-table-header'),
      size: 110
    },
    {
      accessorKey: 'formattedProjectedRevenuePerArea',
      header: getMessage('est-gross-revenue-area', { areaUnit: inferUnit('area').symbol.universal }),
      size: 110
    },
    {
      id: 'remove',
      header: '',
      size: 50,
      cell: ({ row }) => (
        <button className="remove-field-button" onClick={() => remove(row.index)}>
          <Icon icon={FdsIcon.Cancel} size={ComponentSize.Lg} variation={IconVariation.Filled} />
        </button>
      )
    }
  ], [remove]);

  return useReactTable({
    data,
    columns,
    state: {
      expanded,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableMultiSort: true,
    isMultiSortEvent: () => true,
  });
};
